import { computed, effect, Injectable, signal, untracked, inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
// import {
//   DataFactory,
//   UnderwriterSubmissionPayload,
//   UnderwriterSubmissionRecord,
//   UnderwriterSubmissionResponse
// } from './DataFactory';
  import { AuthService } from '../../../core/auth/auth.service';
import { DataAggregationService } from '../../../core/services/data-aggregation.service';
import {AMS , UWI} from './submission.types'



const DEFAULT_UNDERWRITER_SUBMISSION_PAYLOAD: Omit<AMS.UnderwriterSubmissionPayload, 'submission_id' | 'user_id'> = {
  page: 1,
  limit: 10,
  filter_flag: 1,
  underwriter: '',
  underwriter_id: '',
  include_deleted: 0,
  quote_desc: '',
  policy_number: '',
  status: '',
  submit_date: '',
  effective_date: '',
  premium: '',
  insured_id: '',
  dot_number: '',
  insured_name: '',
  agency: '',
  claim_count: '',
  treaty_description: '',
  exp_date: '',
  cancellation_date: '',
  lob: '',
  risk_company: '',
  source_portal: '',
  agency_id: '',
  agency_state: '',
  treaty_id_int: '',
  status_id_int: [1, 3, 5, 7, 17, 18, 27, 28, 29, 30,62,4], // 4 for Declinne status
  short_name: [499, 485, 492, 495,483],
  quote_id: '',
  risk_appetite: '',
  bind_propensity: '',
  data_quality_score: ''
};

const PROCESSED_DETAIL_REQUEST_CONCURRENCY = 3;

@Injectable({ providedIn: 'root' })
export class ProcessedSubmissionsService {
  private readonly dataFactory = inject(DataAggregationService);
  private readonly auth = inject(AuthService);

  private readonly processedSubmissionIdsState = signal<string[]>([]);
  private readonly locallyMarkedProcessedSubmissionIdsState = signal<string[]>([]);
  private readonly processedSubmissionRecordsState = signal<UWI.UnderwriterSubmissionRecord[]>([]);
  private loadedForUserId: number | null = null;
  private readonly requestInFlightState = signal(false);
  private readonly detailRequestsInFlight = new Set<string>();
  private readonly pendingDetailRequests: Array<{ submissionId: string; userId: number; batchId: number }> = [];
  private readonly detailRequestCountState = signal(0);
  private detailsRequestBatch = 0;
  private observedAuthUserId: number | null = null;

  readonly processedSubmissionIds = this.processedSubmissionIdsState.asReadonly();
  readonly locallyMarkedProcessedSubmissionIds = this.locallyMarkedProcessedSubmissionIdsState.asReadonly();
  readonly processedSubmissionRecords = this.processedSubmissionRecordsState.asReadonly();
  readonly loadedProcessedSubmissionCount = computed(() => {
    const loadedSubmissionIds = new Set(
      this.processedSubmissionRecordsState()
        .map((record) => this.readSubmissionId(record))
        .filter((submissionId) => submissionId.length > 0)
    );

    return loadedSubmissionIds.size;
  });
  readonly processedSubmissionSummary = computed(() => {
    const total = this.processedSubmissionIdsState().length;
    const loaded = this.loadedProcessedSubmissionCount();
    const pending = Math.max(this.detailRequestCountState(), 0);
    const loading = this.requestInFlightState() || pending > 0;

    return {
      total,
      loaded,
      pending,
      loading
    };
  });

  constructor() {
    effect(() => {
      const userId = this.auth.userId();
      const isAuthenticated = this.auth.isAuthenticated();

      if (!isAuthenticated || userId === null) {
        if (this.observedAuthUserId !== null || this.loadedForUserId !== null || this.requestInFlightState()) {
          untracked(() => this.reset());
        }

        this.observedAuthUserId = null;
        return;
      }

      if (this.observedAuthUserId !== userId) {
        untracked(() => this.reset());
        this.observedAuthUserId = userId;
      }
    });
  }

  hasSubmission(submissionId: string | number | null | undefined): boolean {
    const normalizedSubmissionId = this.normalizeSubmissionId(submissionId);

    return normalizedSubmissionId.length > 0
      && this.processedSubmissionIdsState().includes(normalizedSubmissionId);
  }

  isLocallyMarkedProcessed(submissionId: string | number | null | undefined): boolean {
    const normalizedSubmissionId = this.normalizeSubmissionId(submissionId);

    return normalizedSubmissionId.length > 0
      && this.locallyMarkedProcessedSubmissionIdsState().includes(normalizedSubmissionId);
  }

  markSubmissionProcessed(submissionId: string | number | null | undefined): void {
    const normalizedSubmissionId = this.normalizeSubmissionId(submissionId);

    if (!normalizedSubmissionId) {
      return;
    }

    this.locallyMarkedProcessedSubmissionIdsState.update((submissionIds) => {
      if (submissionIds.includes(normalizedSubmissionId)) {
        return submissionIds;
      }

      return [...submissionIds, normalizedSubmissionId];
    });

    this.processedSubmissionIdsState.update((submissionIds) => {
      if (submissionIds.includes(normalizedSubmissionId)) {
        return submissionIds;
      }

      return [...submissionIds, normalizedSubmissionId];
    });

    const userId = this.auth.userId();

    if (userId !== null) {
      this.loadSubmissionResponse(normalizedSubmissionId, userId, this.detailsRequestBatch);
    }
  }

  reload(): void {
    const userId = this.auth.userId();

    if (userId === null) {
      this.reset();
      return;
    }

    this.loadedForUserId = null;
    this.loadProcessedSubmissions(userId);
  }

  ensureLoaded(): void {
    const userId = this.auth.userId();

    if (userId === null || !this.auth.isAuthenticated()) {
      return;
    }

    if (this.loadedForUserId === userId || this.requestInFlightState()) {
      return;
    }

    this.loadProcessedSubmissions(userId);
  }

  isLoadedForCurrentUser(): boolean {
    const userId = this.auth.userId();

    return userId !== null && this.loadedForUserId === userId;
  }

  private loadProcessedSubmissions(userId: number): void {
    this.requestInFlightState.set(true);

    this.dataFactory.getSubmissions().pipe(
      finalize(() => {
        this.requestInFlightState.set(false);
      })
    ).subscribe({
      next: (response: UWI.ProcessedSubmissionApiResponse) => {
        const submissionIds = this.extractSubmissionIds(response);
        this.processedSubmissionIdsState.set(submissionIds);
        this.syncProcessedSubmissionRecords(submissionIds, userId);
        this.loadedForUserId = userId;
      },
      error: () => {
        this.processedSubmissionIdsState.set([]);
        this.processedSubmissionRecordsState.set([]);
        this.loadedForUserId = null;
      }
    });
  }

  private extractSubmissionIds(response: UWI.ProcessedSubmissionApiResponse): string[] {
    const records = response.submission_data ?? {};

    return Object.keys(records)
      .map((submissionId) => submissionId.trim())
      .filter((submissionId) => submissionId.length > 0);
  }

  private syncProcessedSubmissionRecords(submissionIds: string[], userId: number): void {
    this.pendingDetailRequests.length = 0;
    const allowedSubmissionIds = new Set(submissionIds);
    this.detailsRequestBatch += 1;

    this.processedSubmissionRecordsState.update((records) =>
      this.sortSubmissionRecords(
        records.filter((record) => allowedSubmissionIds.has(this.readSubmissionId(record)))
      )
    );

    for (const submissionId of submissionIds) {
      if (this.detailRequestsInFlight.has(submissionId) || this.hasResponse(submissionId)) {
        continue;
      }

      this.pendingDetailRequests.push({
        submissionId,
        userId,
        batchId: this.detailsRequestBatch
      });
    }

    this.updateDetailRequestCount();
    this.drainPendingDetailRequests();
  }

  private loadSubmissionResponse(submissionId: string, userId: number, batchId: number): void {
    if (this.detailRequestsInFlight.has(submissionId) || this.hasResponse(submissionId)) {
      return;
    }

    this.detailRequestsInFlight.add(submissionId);
    this.updateDetailRequestCount();

    this.dataFactory.loadSubmissions(this.buildSubmissionPayload(submissionId, userId)).pipe(
      finalize(() => {
        this.detailRequestsInFlight.delete(submissionId);
        this.updateDetailRequestCount();
        this.drainPendingDetailRequests();
      })
    ).subscribe({
      next: (response) => {
        if (batchId !== this.detailsRequestBatch) {
          return;
        }

        const records = this.extractSubmissionRecords(response);

        if (records.length === 0) {
          return;
        }

        this.processedSubmissionRecordsState.update((items) => {
          const nextItems = items.filter((item) => this.readSubmissionId(item) !== submissionId);
          nextItems.push(...records);
          return this.sortSubmissionRecords(nextItems);
        });
      },
      error: () => {
        // Keep the background hydration silent.
      }
    });
  }

  private drainPendingDetailRequests(): void {
    while (
      this.detailRequestsInFlight.size < PROCESSED_DETAIL_REQUEST_CONCURRENCY
      && this.pendingDetailRequests.length > 0
    ) {
      const nextRequest = this.pendingDetailRequests.shift();

      if (!nextRequest) {
        break;
      }

      if (nextRequest.batchId !== this.detailsRequestBatch) {
        continue;
      }

      if (this.detailRequestsInFlight.has(nextRequest.submissionId) || this.hasResponse(nextRequest.submissionId)) {
        continue;
      }

      this.loadSubmissionResponse(nextRequest.submissionId, nextRequest.userId, nextRequest.batchId);
    }
  }

  private updateDetailRequestCount(): void {
    this.detailRequestCountState.set(this.detailRequestsInFlight.size + this.pendingDetailRequests.length);
  }

  private buildSubmissionPayload(submissionId: string, userId: number): AMS.UnderwriterSubmissionPayload {
    return {
      ...DEFAULT_UNDERWRITER_SUBMISSION_PAYLOAD,
      submission_id: submissionId,
      user_id: userId
    };
  }

  private hasResponse(submissionId: string): boolean {
    return this.processedSubmissionRecordsState().some((item) => this.readSubmissionId(item) === submissionId);
  }

  private extractSubmissionRecords(response: UWI.UnderwriterSubmissionResponse): UWI.UnderwriterSubmissionRecord[] {
    return Array.isArray(response.data?.records)
      ? response.data.records
      : [];
  }

  private readSubmissionId(record: UWI.UnderwriterSubmissionRecord): string {
    const submissionId = record['submission_id'];

    if (typeof submissionId === 'string') {
      return submissionId.trim();
    }

    if (typeof submissionId === 'number' && Number.isFinite(submissionId)) {
      return String(submissionId);
    }

    return '';
  }

  private sortSubmissionRecords(records: UWI.UnderwriterSubmissionRecord[]): UWI.UnderwriterSubmissionRecord[] {
    return [...records].sort((left, right) => this.compareSubmissionRecordsBySubmitDate(left, right));
  }

  private compareSubmissionRecordsBySubmitDate(
    left: UWI.UnderwriterSubmissionRecord,
    right: UWI.UnderwriterSubmissionRecord
  ): number {
    const leftDate = this.readSubmissionTimestamp(left);
    const rightDate = this.readSubmissionTimestamp(right);

    if (leftDate !== rightDate) {
      return rightDate - leftDate;
    }

    return this.readSubmissionId(left).localeCompare(this.readSubmissionId(right), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }

  private readSubmissionTimestamp(record: UWI.UnderwriterSubmissionRecord): number {
    const rawValue = this.readSubmissionDate(record);

    if (!rawValue) {
      return Number.POSITIVE_INFINITY;
    }

    const timestamp = Date.parse(rawValue);

    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }

    return Number.POSITIVE_INFINITY;
  }

  private readSubmissionDate(record: UWI.UnderwriterSubmissionRecord): string {
    const keys = ['submit_date', 'created_at', 'created_date', 'createdon'];

    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }

    return '';
  }

  private normalizeSubmissionId(submissionId: string | number | null | undefined): string {
    if (typeof submissionId === 'string') {
      return submissionId.trim();
    }

    if (typeof submissionId === 'number' && Number.isFinite(submissionId)) {
      return String(submissionId);
    }

    return '';
  }

  private reset(): void {
    this.processedSubmissionIdsState.set([]);
    this.locallyMarkedProcessedSubmissionIdsState.set([]);
    this.processedSubmissionRecordsState.set([]);
    this.loadedForUserId = null;
    this.requestInFlightState.set(false);
    this.detailRequestsInFlight.clear();
    this.pendingDetailRequests.length = 0;
    this.detailRequestCountState.set(0);
    this.detailsRequestBatch += 1;
  }
}
